if (!customElements.get('cart-drawer')) {
  class CartDrawer extends HTMLElement {
    constructor() {
      super();
      this.isInitialized = false;
      this.setHeaderCartIconAccessibility();
    }

    init() {
      if (!this.isInitialized) {
        window.loadTemplateContent(this);

        requestAnimationFrame(() => {
          // Bind various things
          this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
          this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
          this.moveCssLinks();
          this.isInitialized = true;
        });
      }
    }

    reload() {
      const sections = this.getSectionsToRender().map((section) => section.id);
      fetch(`?sections=${sections}`)
        .then((response) => response.json())
        .then((response) => {
          this.renderContents({
            sections: {
              ...response
            }
          }, true);
        })
        .catch((e) => {
          console.error(e);
        });
    }

    moveCssLinks() {
      const cssLinks = this.querySelectorAll('link[rel="stylesheet"][href*=".css"]');
      cssLinks.forEach(link => {
        // Check if the link already exists in the document head
        const href = link.href;
        if (!document.head.querySelector(`link[href="${href}"]`)) {
          // Clone the link element
          const newLink = link.cloneNode(true);
          // Append the cloned link to the document head
          document.head.appendChild(newLink);
        }
      });
    }

    setHeaderCartIconAccessibility() {
      const cartLink = document.querySelector('#cart-icon-bubble');
      if (!cartLink) return;

      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    }

    open(triggeredBy) {
      this.init();

      if (triggeredBy) this.setActiveElement(triggeredBy);
      const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
      if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
      // here the animation doesn't seem to always get triggered. A timeout seem to help
      setTimeout(() => {
        this.classList.add('animate', 'active');
      });

      this.removeDeleteTabIndex();

      const onOpened = () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);

        publish(PUB_SUB_EVENTS.cartOpen);
      };
      // Reduced motion drops the panel transition, so it never sends a transitionend of its own.
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        setTimeout(() => requestAnimationFrame(onOpened));
      } else {
        this.addEventListener('transitionend', onOpened, {once: true});
      }

      document.body.classList.add('overflow-hidden');
    }

    close() {
      this.classList.remove('active');
      removeTrapFocus(this.activeElement);
      document.body.classList.remove('overflow-hidden');
      publish(PUB_SUB_EVENTS.cartClose);
    }

    /**
     * Ensure all remove buttons can be tabbed to
     */
    removeDeleteTabIndex() {
      this.querySelectorAll('.cart-remove-button').forEach((button) => {
        button.removeAttribute('tabindex')
      });
    }

    setSummaryAccessibility(cartDrawerNote) {
      cartDrawerNote.setAttribute('role', 'button');
      cartDrawerNote.setAttribute('aria-expanded', 'false');

      if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
        cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
      }

      cartDrawerNote.addEventListener('click', (event) => {
        event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
      });

      cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
    }

    renderContents(parsedState, preventOpen = false) {
      this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
      this.productId = parsedState.id;
      // A closed drawer is about to slide in, so hold the fill until it has landed.
      const wasOpen = this.classList.contains('active');
      const shippingBarDelay = wasOpen ? 0 : this.getEnterDuration();
      const previousLines = this.getLineQuantities();
      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);

        if (!sectionElement) return;
        const shippingBarFill = readShippingBarFill(sectionElement);
        sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
        glideShippingBar(sectionElement, shippingBarFill, shippingBarDelay);
      });
      const addedLines = this.markAddedLines(previousLines);
      // Next frame, so callers have dropped is-empty and the footer is laid out.
      if (addedLines.length) requestAnimationFrame(() => this.revealLine(addedLines[0], wasOpen));

      setTimeout(() => {
        window.loadTemplateContent(this);
        this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      });

      this.removeDeleteTabIndex();

      if (theme.settings.cartDrawer.atcAction === 'drawer' && !preventOpen) {
        setTimeout(() => {
          this.open();
        });
      }
    }

    getLineQuantities() {
      const lines = new Map();
      this.querySelectorAll('.cart-item[data-key]').forEach((row) => {
        lines.set(row.dataset.key, Number(row.dataset.quantity));
      });
      return lines;
    }

    // Every add path re-renders the drawer, so a line that is new or grew is the one just added.
    markAddedLines(previousLines) {
      const added = [...this.querySelectorAll('.cart-item[data-key]')].filter(
        (row) => Number(row.dataset.quantity) > (previousLines.get(row.dataset.key) || 0)
      );
      added.forEach((row) => {
        row.classList.add('cart-item--added');
        const clear = (event) => {
          if (event.target !== row || !event.pseudoElement) return;
          row.classList.remove('cart-item--added');
          row.removeEventListener('animationend', clear);
        };
        row.addEventListener('animationend', clear);
      });
      return added;
    }

    // Scroll just enough that the line clears the footer, keeping as much of the shipping bar as possible.
    revealLine(row, smooth) {
      let scroller = row.parentElement;
      while (scroller && scroller !== this && !/auto|scroll/.test(getComputedStyle(scroller).overflowY)) {
        scroller = scroller.parentElement;
      }
      if (!scroller || scroller === this) return;
      const view = scroller.getBoundingClientRect();
      const line = row.getBoundingClientRect();
      const hidden = line.bottom - view.bottom;
      if (hidden <= 0) return;
      scroller.scrollBy({
        top: Math.min(hidden, line.top - view.top),
        behavior: smooth && !window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'auto',
      });
    }

    // The closed panel carries the exit timing, so the slide-in length comes from the token.
    getEnterDuration() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return 0;
      const value = getComputedStyle(this).getPropertyValue('--cart-drawer-enter').trim();
      return parseFloat(value) * (value.endsWith('ms') ? 1 : 1000) || 0;
    }

    getSectionInnerHTML(html, selector = '.shopify-section') {
      html = html.replace(/<template\b[^>]*>|<\/template>/g, '');
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
    }

    getSectionsToRender() {
      return [
        {
          id: 'cart-drawer',
          selector: '#CartDrawer',
        },
        {
          id: 'cart-icon-bubble',
        },
        {
          id: 'cart-drawer-addons',
          section: 'cart-drawer-addons',
          selector: '#shopify-section-cart-drawer-addons',
        },
      ];
    }

    getSectionDOM(html, selector = '.shopify-section') {
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    }

    setActiveElement(element) {
      this.activeElement = element;
    }
  }

  customElements.define('cart-drawer', CartDrawer);
}

if (!customElements.get('cart-drawer-items')) {
  customElements.whenDefined('cart-items').then(() => {
    class CartDrawerItems extends CartItems {
      getSectionsToRender() {
        return [
          {
            id: 'CartDrawer',
            section: 'cart-drawer',
            selector: '.drawer__inner',
          },
          {
            id: 'cart-icon-bubble',
            section: 'cart-icon-bubble',
            selector: '.shopify-section',
          },
          {
            id: 'shopify-section-cart-drawer-addons',
            section: 'cart-drawer-addons',
            selector: '.cart-addons-host',
          },
        ];
      }
    }

    customElements.define('cart-drawer-items', CartDrawerItems);
  });
}
